import type {
  FieldComponents,
  UploadedFile,
} from '@/shared/components/form-fields/types';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { toaster } from '@/shared/ui/toaster.component';
import { Button, Dialog, Text } from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { useNavigate } from '@tanstack/react-router';
import { CircleCheck, CircleX } from 'lucide-react';
import { useState } from 'react';
import {
  useDecisionInspectionForm,
  withForm,
} from '../../hooks/use-decision-inspection-form.hook';
import { useDecisionInspection } from '../../hooks/use-decision-inspection.hook';

type DecisionValue = null | 'ACCEPT' | 'REJECT';

type DecisionFormValues = {
  status: DecisionValue;
  acceptance: string;
  rejection: string;
  reasonAttachments: UploadedFile[];
  addSuggestions: boolean;
  suggestion: string;
  attachments: UploadedFile[];
};


const PDF_TYPE = 'application/pdf';
const DOC_TYPE = 'application/msword';
const DOCX_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const JPEG_TYPE = 'image/jpeg';
const PNG_TYPE = 'image/png';

// Motivo de recusa/aceite aceita documentos e imagens.
const REASON_FILE_TYPES = new Set([
  PDF_TYPE,
  DOC_TYPE,
  DOCX_TYPE,
  JPEG_TYPE,
  PNG_TYPE,
]);
const REASON_ACCEPT = `${PDF_TYPE}, ${DOC_TYPE}, ${DOCX_TYPE}, ${JPEG_TYPE}, ${PNG_TYPE}`;
const REASON_FORMATS_LABEL = 'PDF, DOC, DOCX, JPG, JPEG, PNG';

// Sugestão de melhoria aceita apenas documentos.
const SUGGESTION_FILE_TYPES = new Set([PDF_TYPE, DOC_TYPE, DOCX_TYPE]);
const SUGGESTION_ACCEPT = `${PDF_TYPE}, ${DOC_TYPE}, ${DOCX_TYPE}`;
const SUGGESTION_FORMATS_LABEL = 'PDF, DOC, DOCX';

const MAX_ATTACHMENTS_COUNT = 5;
const MAX_ATTACHMENT_SIZE_MB = 50;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

interface Step2Tab2DecisionProps {
  policyId: string;
  inspectionId: string;
  isTechnicalOpinionFinalized: boolean;
  canWrite: boolean;
  onCancel: () => void;
}

function validateAttachments(
  attachments: UploadedFile[] | undefined,
  allowedTypes: Set<string>
): string | undefined {
  if (!attachments?.length) return undefined;

  if (attachments.length > MAX_ATTACHMENTS_COUNT) {
    return `Quantidade máxima de ${MAX_ATTACHMENTS_COUNT} arquivos.`;
  }

  const invalidType = attachments.find(
    file => !allowedTypes.has(file.file.type)
  );
  if (invalidType) {
    return `Tipo de arquivo não permitido: ${invalidType.name}`;
  }

  const invalidSize = attachments.find(
    file => file.size > MAX_ATTACHMENT_SIZE_BYTES
  );
  if (invalidSize) {
    return `O arquivo ${invalidSize.name} excede ${MAX_ATTACHMENT_SIZE_MB}MB.`;
  }

  return undefined;
}

export function Step2Tab2Decision({
  policyId,
  inspectionId,
  isTechnicalOpinionFinalized,
  canWrite,
  onCancel,
}: Step2Tab2DecisionProps) {
  const navigate = useNavigate();
  const decideMutation = useDecisionInspection(policyId, inspectionId);
  const canAcceptProposal = isTechnicalOpinionFinalized;
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const mainForm = useDecisionInspectionForm({
    defaultValues: {
      status: null,
      acceptance: '',
      rejection: '',
      reasonAttachments: [],
      addSuggestions: false,
      suggestion: '',
      attachments: [],
    },
    onSubmit: async ({ value }: { value: DecisionFormValues }) => {
      try {
        const description =
          value.status === 'ACCEPT' ? value.acceptance : value.rejection;

        await decideMutation.mutateAsync({
          status: value.status === 'ACCEPT' ? 'accepted' : 'rejected',
          description,
          finalDecisionAttachments: value.reasonAttachments,
          improvementLetter: value.addSuggestions
            ? value.suggestion
            : undefined,
          improvementLetterAttachments: value.addSuggestions
            ? value.attachments
            : undefined,
        });

        toaster.create(
          value.status === 'ACCEPT'
            ? {
                type: 'success',
                title: 'Proposta aceita',
                description: 'A proposta foi enviada para emissão da apólice.',
              }
            : {
                type: 'success',
                title: 'Proposta recusada',
                description: 'A decisão foi registrada com sucesso.',
              }
        );

        mainForm.reset();

        void navigate({ to: '/inspections' });
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao confirmar decisão',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      }
    },
  });

  return (
    <div className='space-y-8'>
      <div className='flex flex-col gap-4 bg-white shadow-xl rounded-2xl py-12 px-8'>
        <p>Decisão final</p>

        <FollowUpInspectionForm
          canAcceptProposal={canAcceptProposal}
          form={mainForm}
          disabled={!canWrite}
        />
      </div>

      <div className='flex flex-row gap-4 justify-between items-center'>
        <Button variant='ghost' size='md' onClick={onCancel}>
          Cancelar
        </Button>

        {canWrite && (
          <mainForm.Subscribe
            selector={(state: {
              values: { status: null | 'ACCEPT' | 'REJECT' };
              isSubmitting: boolean;
            }) => ({
              status: state.values.status,
              isSubmitting: state.isSubmitting,
            })}
            children={({
              status,
              isSubmitting,
            }: {
              status: null | 'ACCEPT' | 'REJECT';
              isSubmitting: boolean;
            }) => (
              <>
                <Button
                  variant='primary'
                  size='md'
                  disabled={
                    status === null ||
                    isSubmitting ||
                    (status === 'ACCEPT' && !canAcceptProposal)
                  }
                  onClick={() => setIsConfirmDialogOpen(true)}
                >
                  Confirmar
                </Button>

                <Dialog.Root
                  open={isConfirmDialogOpen}
                  onOpenChange={(details: { open: boolean }) =>
                    setIsConfirmDialogOpen(details.open)
                  }
                >
                  <Dialog.Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                      <Dialog.Content className='w-full max-w-md'>
                        <Dialog.Header>
                          <Dialog.Title>
                            {status === 'ACCEPT' ? (
                              <CircleCheck className='size-6' />
                            ) : (
                              <CircleX className='size-6' />
                            )}
                            Confirmar decisão
                          </Dialog.Title>
                          <Dialog.CloseTrigger asChild>
                            <Dialog.CloseButton />
                          </Dialog.CloseTrigger>
                        </Dialog.Header>
                        <Dialog.Body>
                          <Text>
                            {status === 'ACCEPT'
                              ? 'Ao confirmar, esta proposta será aceita e enviada para o processo de emissão da apólice.'
                              : 'Ao confirmar, esta proposta será recusada e a carta de recusa será enviada.'}
                          </Text>
                          <Text>Esta ação não poderá ser desfeita.</Text>
                        </Dialog.Body>
                        <Dialog.Footer>
                          <Dialog.CloseTrigger asChild>
                            <Button
                              variant='ghost'
                              size='md'
                              disabled={isSubmitting}
                            >
                              Cancelar
                            </Button>
                          </Dialog.CloseTrigger>
                          <Button
                            variant='primary'
                            size='md'
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            onClick={async () => {
                              await mainForm.handleSubmit();
                              setIsConfirmDialogOpen(false);
                            }}
                          >
                            Confirmar{' '}
                            {status === 'ACCEPT' ? 'Aceitação' : 'Recusa'}
                          </Button>
                        </Dialog.Footer>
                      </Dialog.Content>
                    </Dialog.Positioner>
                  </Dialog.Portal>
                </Dialog.Root>
              </>
            )}
          />
        )}
      </div>
    </div>
  );
}

const formOpts = formOptions({
  defaultValues: {
    status: null,
    acceptance: '',
    rejection: '',
    reasonAttachments: [],
    addSuggestions: false,
    suggestion: '',
    attachments: [],
  },
});

const FollowUpInspectionForm = withForm({
  ...formOpts,
  props: {
    canAcceptProposal: false,
    disabled: false,
  },
  render: ({
    form,
    canAcceptProposal,
    disabled,
  }: {
    // `withForm` do TanStack não expõe um tipo público compatível com o
    // ReturnType do hook; o resto do projeto usa `any` aqui pelo mesmo motivo.
    form: any;
    canAcceptProposal: boolean;
    disabled: boolean;
  }) => {
    const decisionOptions = [
      ...(canAcceptProposal
        ? [
            {
              label: 'Aceitar Proposta',
              value: 'ACCEPT',
              icon: <CircleCheck className='size-6' />,
              description: 'Segue para emissão da apólice.',
            },
          ]
        : []),
      {
        label: 'Recusar Proposta',
        value: 'REJECT',
        icon: <CircleX className='size-6' />,
        description: 'Carta de recusa será enviada',
      },
    ];

    return (
      <div className='flex flex-col gap-y-4'>
        <form.AppField
          name='status'
          validators={{
            onChange: ({ value }: { value: string | undefined }) => {
              if (!value) return 'O status é obrigatório';
              if (value === 'ACCEPT' && !canAcceptProposal) {
                return 'Aceitação disponível apenas com Parecer Técnico finalizado.';
              }
              return undefined;
            },
            onSubmit: ({ value }: { value: string | undefined }) => {
              if (!value) return 'O status é obrigatório';
              if (value === 'ACCEPT' && !canAcceptProposal) {
                return 'Aceitação disponível apenas com Parecer Técnico finalizado.';
              }
              return undefined;
            },
          }}
          children={(field: FieldComponents) => (
            <field.RadioCardField
              required
              options={decisionOptions}
              disabled={disabled}
            />
          )}
        />
        {!canAcceptProposal && (
          <Text className='text-sm text-alba-gray-600'>
            “Aceitar Proposta” só é liberado após o Parecer Técnico finalizado.
          </Text>
        )}

        <form.Subscribe
          selector={(state: {
            values: { status: 'ACCEPT' | 'REJECT' | null };
          }) => ({
            status: state.values.status,
          })}
          children={({ status }: { status: null | 'ACCEPT' | 'REJECT' }) => {
            if (status === 'REJECT') {
              return (
                <form.AppField
                  name='rejection'
                  validators={{
                    onChange: ({ value }: { value: string | null }) => {
                      if (!value?.trim()) return 'A rejeição é obrigatória';
                      return undefined;
                    },
                    onSubmit: ({ value }: { value: string | null }) => {
                      if (!value?.trim()) return 'A rejeição é obrigatória';
                      return undefined;
                    },
                  }}
                  children={(field: FieldComponents) => (
                    <>
                      <field.TextareaField
                        label='Motivo de Recusa'
                        placeholder='Digite o motivo de recusa que será enviado para o segurado, corretor e executivo comercial'
                        helperMessage='Descreva o motivo da recusa da que será utilizado na carta de recusa'
                        required
                        rows={5}
                        maxLength={FIELD_MAX_LENGTHS.textarea}
                        disabled={disabled}
                      />

                      <form.AppField
                        name='reasonAttachments'
                        validators={{
                          onChange: ({
                            value,
                          }: {
                            value: UploadedFile[] | undefined;
                          }) => validateAttachments(value, REASON_FILE_TYPES),
                          onSubmit: ({
                            value,
                          }: {
                            value: UploadedFile[] | undefined;
                          }) => validateAttachments(value, REASON_FILE_TYPES),
                        }}
                        children={(field: FieldComponents) => (
                          <field.UploadField
                            label='Anexos do motivo de recusa'
                            accept={REASON_ACCEPT}
                            helperText={`Formatos aceitos: ${REASON_FORMATS_LABEL}. Máx. ${MAX_ATTACHMENTS_COUNT} arquivos de até ${MAX_ATTACHMENT_SIZE_MB}MB.`}
                            multiple
                            disabled={disabled}
                          />
                        )}
                      />

                      <form.AppField
                        name='addSuggestions'
                        children={(field: FieldComponents) => (
                          <field.CheckboxField
                            label='Adicionar Sugestão de Melhoria?'
                            disabled={disabled}
                            onChange={(checked: boolean) => {
                              if (!checked) {
                                form.setFieldValue('suggestion', '');
                                form.setFieldValue('attachments', []);
                              }
                            }}
                          />
                        )}
                      />

                      <form.Subscribe
                        selector={(state: {
                          values: { addSuggestions: boolean };
                        }) => ({
                          addSuggestions: state.values.addSuggestions,
                        })}
                        children={({
                          addSuggestions,
                        }: {
                          addSuggestions: boolean;
                        }) => {
                          if (!addSuggestions) {
                            return null;
                          }

                          return (
                            <>
                              <form.AppField
                                name='suggestion'
                                validators={{
                                  onChange: ({
                                    value,
                                  }: {
                                    value: string | undefined;
                                  }) => {
                                    if (!form.state.values.addSuggestions) {
                                      return undefined;
                                    }
                                    if (!value?.trim()) {
                                      return 'A carta de melhorias é obrigatória.';
                                    }
                                    return undefined;
                                  },
                                  onSubmit: ({
                                    value,
                                  }: {
                                    value: string | undefined;
                                  }) => {
                                    if (!form.state.values.addSuggestions) {
                                      return undefined;
                                    }
                                    if (!value?.trim()) {
                                      return 'A carta de melhorias é obrigatória.';
                                    }
                                    return undefined;
                                  },
                                }}
                                children={(field: FieldComponents) => (
                                  <field.TextareaField
                                    label='Sugestão de Melhorias'
                                    required
                                    placeholder='Digite as observações para a carta de melhoria'
                                    rows={5}
                                    maxLength={FIELD_MAX_LENGTHS.textarea}
                                    disabled={disabled}
                                  />
                                )}
                              />

                              <form.AppField
                                name='attachments'
                                validators={{
                                  onChange: ({
                                    value,
                                  }: {
                                    value: UploadedFile[] | undefined;
                                  }) =>
                                    validateAttachments(
                                      value,
                                      SUGGESTION_FILE_TYPES
                                    ),
                                  onSubmit: ({
                                    value,
                                  }: {
                                    value: UploadedFile[] | undefined;
                                  }) =>
                                    validateAttachments(
                                      value,
                                      SUGGESTION_FILE_TYPES
                                    ),
                                }}
                                children={(field: FieldComponents) => (
                                  <field.UploadField
                                    label='Anexos'
                                    accept={SUGGESTION_ACCEPT}
                                    helperText={`Formatos aceitos: ${SUGGESTION_FORMATS_LABEL}. Máx. ${MAX_ATTACHMENTS_COUNT} arquivos de até ${MAX_ATTACHMENT_SIZE_MB}MB.`}
                                    multiple
                                    disabled={disabled}
                                  />
                                )}
                              />
                            </>
                          );
                        }}
                      />
                    </>
                  )}
                />
              );
            }

            if (status === 'ACCEPT') {
              return (
                <>
                  <form.AppField
                    name='acceptance'
                    children={(field: FieldComponents) => (
                      <field.TextareaField
                        label='Parecer de aceitação'
                        placeholder='Digite as observações sobre a inspeção'
                        rows={5}
                        maxLength={FIELD_MAX_LENGTHS.textarea}
                        disabled={disabled}
                      />
                    )}
                  />

                  <form.AppField
                    name='reasonAttachments'
                    validators={{
                      onChange: ({
                        value,
                      }: {
                        value: UploadedFile[] | undefined;
                      }) => validateAttachments(value, REASON_FILE_TYPES),
                      onSubmit: ({
                        value,
                      }: {
                        value: UploadedFile[] | undefined;
                      }) => validateAttachments(value, REASON_FILE_TYPES),
                    }}
                    children={(field: FieldComponents) => (
                      <field.UploadField
                        label='Anexos do motivo de aceite'
                        accept={REASON_ACCEPT}
                        helperText={`Formatos aceitos: ${REASON_FORMATS_LABEL}. Máx. ${MAX_ATTACHMENTS_COUNT} arquivos de até ${MAX_ATTACHMENT_SIZE_MB}MB.`}
                        multiple
                        disabled={disabled}
                      />
                    )}
                  />

                  <form.AppField
                    name='addSuggestions'
                    children={(field: FieldComponents) => (
                      <field.CheckboxField
                        label='Adicionar Sugestão de Melhoria?'
                        disabled={disabled}
                        onChange={(checked: boolean) => {
                          if (!checked) {
                            form.setFieldValue('suggestion', '');
                            form.setFieldValue('attachments', []);
                          }
                        }}
                      />
                    )}
                  />

                  <form.Subscribe
                    selector={(state: {
                      values: { addSuggestions: boolean };
                    }) => ({
                      addSuggestions: state.values.addSuggestions,
                    })}
                    children={({
                      addSuggestions,
                    }: {
                      addSuggestions: boolean;
                    }) => {
                      if (!addSuggestions) {
                        return null;
                      }

                      return (
                        <>
                          <form.AppField
                            name='suggestion'
                            validators={{
                              onChange: ({
                                value,
                              }: {
                                value: string | undefined;
                              }) => {
                                if (!form.state.values.addSuggestions) {
                                  return undefined;
                                }
                                if (!value?.trim()) {
                                  return 'A carta de melhorias é obrigatória.';
                                }
                                return undefined;
                              },
                              onSubmit: ({
                                value,
                              }: {
                                value: string | undefined;
                              }) => {
                                if (!form.state.values.addSuggestions) {
                                  return undefined;
                                }
                                if (!value?.trim()) {
                                  return 'A carta de melhorias é obrigatória.';
                                }
                                return undefined;
                              },
                            }}
                            children={(field: FieldComponents) => (
                              <field.TextareaField
                                label='Sugestão de Melhorias'
                                required
                                placeholder='Digite as observações para a carta de melhoria'
                                rows={5}
                                maxLength={FIELD_MAX_LENGTHS.textarea}
                                disabled={disabled}
                              />
                            )}
                          />

                          <form.AppField
                            name='attachments'
                            validators={{
                              onChange: ({
                                value,
                              }: {
                                value: UploadedFile[] | undefined;
                              }) =>
                                validateAttachments(
                                  value,
                                  SUGGESTION_FILE_TYPES
                                ),
                              onSubmit: ({
                                value,
                              }: {
                                value: UploadedFile[] | undefined;
                              }) =>
                                validateAttachments(
                                  value,
                                  SUGGESTION_FILE_TYPES
                                ),
                            }}
                            children={(field: FieldComponents) => (
                              <field.UploadField
                                label='Anexos'
                                accept={SUGGESTION_ACCEPT}
                                helperText={`Formatos aceitos: ${SUGGESTION_FORMATS_LABEL}. Máx. ${MAX_ATTACHMENTS_COUNT} arquivos de até ${MAX_ATTACHMENT_SIZE_MB}MB.`}
                                multiple
                                disabled={disabled}
                              />
                            )}
                          />
                        </>
                      );
                    }}
                  />
                </>
              );
            }

            return null;
          }}
        />
      </div>
    );
  },
});
